// src/services/AutoProctorService.js
class AutoProctorService {
    constructor() {
        this.apInstance = null
        this.initialized = false
        this.screenStream = null
        this.cameraStream = null
        this.status = {
            camera: false,
            microphone: false,
            screen: false,
            fullScreenShared: false,
            proctorStarted: false
        }
    }

    /**
     * =========================
     * BROWSER SUPPORT CHECK
     * =========================
     */
    isSupported() {
        if (!window.isSecureContext) {
            return {
                success: false,
                message: 'Gunakan koneksi HTTPS.'
            }
        }

        if (!navigator.mediaDevices) {
            return {
                success: false,
                message: 'API Media Devices tidak mendukung.'
            }
        }

        if (!navigator.mediaDevices.getUserMedia) {
            return {
                success: false,
                message: 'API Kamera tidak mendukung.'
            }
        }

        if (!navigator.mediaDevices.getDisplayMedia) {
            return {
                success: false,
                message: 'Share Screen tidak mendukung.'
            }
        }

        return { success: true }
    }

    async checkPermissions() {
        try {
            const support = this.isSupported()

            if (!support.success) {
                return {
                    success: false,
                    message: support.message
                }
            }

            if (navigator.permissions) {
                const cam = await navigator.permissions.query({ name: 'camera' })
                const mic = await navigator.permissions.query({ name: 'microphone' })

                if (cam.state === 'denied' || mic.state === 'denied') {
                    return {
                        success: false,
                        needManualEnable: true,
                        message: 'Camera or microphone blocked in browser settings.'
                    }
                }
            }

            this.cameraStream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            })

            this.status.camera = true
            this.status.microphone = true

            return { success: true }

        } catch (error) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    generateCredentials(clientId, clientSecret) {
        const testAttemptId = Math.random().toString(36).slice(2, 10)

        const hash = window.CryptoJS.HmacSHA256(
            testAttemptId,
            clientSecret
        )

        return {
            clientId: clientId,
            testAttemptId: testAttemptId,
            hashedTestAttemptId:
                window.CryptoJS.enc.Base64.stringify(hash)
        }
    }

    generateResultsCredentials(
        clientId,
        clientSecret,
        testAttemptId
    ) {
        // session hash
        const attemptHash =
            window.CryptoJS.HmacSHA256(
                testAttemptId,
                clientSecret
            )

        // timestamp hash
        const unixTimestamp =
            Math.floor(Date.now() / 1000)

        const timeHash =
            window.CryptoJS.HmacSHA256(
                unixTimestamp.toString(),
                clientSecret
            )

        return {
            clientId: clientId,

            testAttemptId: testAttemptId,
            hashedTestAttemptId:
                window.CryptoJS.enc.Base64.stringify(
                    attemptHash
                ),

            unixTimestamp: unixTimestamp,
            hashedUnixTimestamp:
                window.CryptoJS.enc.Base64.stringify(
                    timeHash
                )
        }
    }


    // INIT AUTOPROCTOR INSTANCE
    createInstance(credentials) {
        if (!window.AutoProctor) {
            throw new Error('AutoProctor SDK not loaded.')
        }

        this.apInstance = new window.AutoProctor(credentials)

        return this.apInstance
    }

    async setup(credentials, options) {
        try {
            if (!this.apInstance) {
                this.createInstance(credentials)
            }

            await this.apInstance.setup(options)

            this.initialized = true

            // setting permission akses
            this.status.camera = true;
            this.status.microphone = true;

            return { success: true }

        } catch (error) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    // Start AutoProctor
    async start() {
        try {
            if (!this.apInstance || !this.initialized) {
                throw new Error("AutoProctor not setup yet")
            }

            await this.apInstance.start()
            return {
                success: true
            }

        } catch (error) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    waitUntilReady(timeout = 30000) {
        // Error codes yang merupakan violation/pelanggaran — bukan fatal startup error.
        // SDK tetap berjalan dan apMonitoringStarted akan tetap ter-dispatch.
        const NON_BLOCKING_ERRORS = new Set([
            4015, // Multiple monitors detected
        ])

        // Kumpulkan violations yang terjadi sebelum realtime listener aktif,
        // agar bisa di-push ke proctorLiveEvents oleh caller setelah startup selesai.
        const startupViolations = []

        return new Promise((resolve) => {

            const cleanup = () => {
                window.removeEventListener("apMonitoringStarted", startHandler)
                window.removeEventListener("apErrorEvent", errorHandler)
            }

            const timer = setTimeout(() => {
                cleanup()
                resolve({
                    success: false,
                    message: "AutoProctor startup timeout",
                    startupViolations
                })
            }, timeout)

            const startHandler = () => {
                clearTimeout(timer)
                cleanup()
                this.status.proctorStarted = true
                resolve({ success: true, startupViolations })
            }

            const errorHandler = (e) => {
                const code = e?.detail?.errorCode

                if (NON_BLOCKING_ERRORS.has(code)) {
                    console.warn('[AutoProctor] Non-blocking violation detected during startup (code ' + code + '), continuing...')
                    startupViolations.push({
                        type: "error",
                        code: code,
                        timestamp: new Date().toISOString()
                    })
                    return
                }

                clearTimeout(timer)
                cleanup()
                console.error('[AutoProctor] SDK error event during startup:', e?.detail)
                resolve({
                    success: false,
                    sdkError: true,
                    errorCode: code,
                    message: `SDK error: ${code || 'unknown'}`,
                    startupViolations
                })
            }

            window.addEventListener("apMonitoringStarted", startHandler)
            window.addEventListener("apErrorEvent", errorHandler)
        })
    }

    // Stop AutoProctor
    async stop() {
        try {
            if (this.apInstance && this.status.proctorStarted) {
                try {
                    await this.apInstance.stop()
                } catch (stopError) {
                    // Ekstrak pesan dari Error object maupun plain string/value
                    const errMsg = (stopError?.message || String(stopError)).toLowerCase()

                    // SDK melempar ini saat start() dipanggil tapi apMonitoringStarted
                    // tidak pernah ter-dispatch secara internal (SDK belum benar-benar berjalan).
                    // Juga terjadi saat forceFullScreen=true dan user keluar dari fullscreen
                    // sehingga SDK menghentikan dirinya sendiri sebelum kita panggil stop().
                    const isNotStartedError =
                        errMsg.includes('start function first') ||
                        errMsg.includes('without setting up') ||
                        errMsg.includes('apmonitoringstarted')

                    if (isNotStartedError) {
                        console.warn('AutoProctor stop skipped - SDK not in started state:', stopError?.message || stopError)
                    } else {
                        // Error lain (misalnya network) — tetap log tapi jangan blokir user
                        console.error('AutoProctor stop unexpected error:', stopError?.message || stopError)
                    }

                    this.status.proctorStarted = false
                    this.status.screen = false
                    return { success: true, neverStarted: true }
                }
            }

            this.status.proctorStarted = false
            this.status.screen = false

            return { success: true }

        } catch (error) {
            return {
                success: false,
                message: error?.message || String(error)
            }
        }
    }


    // Get result JSON
    getResults(clientId, clientSecret, testAttemptId) {
        const credentials =
            this.generateResultsCredentials(
                clientId,
                clientSecret,
                testAttemptId
            )

        const resultInstance =
            new window.AutoProctor(credentials)

        const results =
            resultInstance.getReport()

        return results
    }

    onStopped(callback) {
        window.addEventListener("apMonitoringStopped", callback)
    }

    removeStopped(callback) {
        window.removeEventListener("apMonitoringStopped", callback)
    }

    // Add inside class AutoProctorService

    async clearInstances() {
        try {
            /*
            |--------------------------------------------
            | Stop running instance first (safe mode)
            |--------------------------------------------
            */
            if (this.apInstance && this.status.proctorStarted) {
                try {
                    if (typeof this.apInstance.stop === "function") {
                        await this.apInstance.stop()
                    }
                } catch (e) {
                    console.warn("AutoProctor stop skipped:", e)
                }
            }

            /*
            |--------------------------------------------
            | Stop opened media streams
            |--------------------------------------------
            */
            this.cleanup()

            /*
            |--------------------------------------------
            | Remove report containers from DOM
            |--------------------------------------------
            */
            const selectors = [
                ".ap-report-tab-container",
                ".ap-report-container",
                ".ap-report-wrapper",
                "#ap-report-tab-container",
                "#ap-report-container"
            ]

            selectors.forEach(selector => {
                document.querySelectorAll(selector).forEach(el => el.remove())
            })

            /*
            |--------------------------------------------
            | Reset service state
            |--------------------------------------------
            */
            this.apInstance = null
            this.initialized = false

            this.status = {
                camera: false,
                microphone: false,
                screen: false,
                fullScreenShared: false,
                proctorStarted: false
            }

            return {
                success: true
            }

        } catch (error) {
            return {
                success: false,
                message: error.message
            }
        }
    }
    

    /**
     * =========================
     * SCREEN SHARE CHECK
     * Require FULL SCREEN
     * =========================
     */
    async checkScreenShare(requireFullScreen = true) {
        try {
            this.screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: false
            })

            const track = this.screenStream.getVideoTracks()[0]
            const settings = track.getSettings()

            const displaySurface = settings.displaySurface || null

            this.status.screen = true

            if (requireFullScreen) {
                this.status.fullScreenShared = displaySurface === 'monitor'

                if (!this.status.fullScreenShared) {
                    this.stopScreenShare()

                    return {
                        success: false,
                        screen: true,
                        fullScreen: false,
                        type: displaySurface,
                        message: 'Pastikan membagikan seluruh layar (Entire Screen).'
                    }
                }
            }

            track.onended = () => {
                this.status.screen = false
                this.status.fullScreenShared = false
            }

            return {
                success: true,
                screen: true,
                fullScreen: this.status.fullScreenShared,
                type: displaySurface
            }
        } catch (error) {
            this.status.screen = false
            this.status.fullScreenShared = false

            return {
                success: false,
                screen: false,
                message: error.message
            }
        }
    }

    /**
     * =========================
     * SHOW CAMERA PREVIEW
     * =========================
     */
    async attachCameraPreview(videoElement) {
        if (this.cameraStream && videoElement) {
            videoElement.srcObject = this.cameraStream

            try {
                await videoElement.play()
            } catch (error) {
                console.error(error)
            }
        }
    }

    /**
     * =========================
     * SHOW SCREEN PREVIEW
     * =========================
     */
    async attachScreenPreview(videoElement) {
        if (this.screenStream && videoElement) {
            videoElement.srcObject = this.screenStream

            try {
                await videoElement.play()
            } catch (error) {
                console.error(error)
            }
        }
    }


    /**
     * =========================
     * RUN FULL PRECHECK
     * =========================
     */
    async runFullCheck() {
        const media = await this.checkPermissions()

        if (!media.status) {
            return media
        }

        const screen = await this.checkScreenShare(true)

        if (!screen.status) {
            return screen
        }

        return {
            status: true,
            message: 'All permissions granted.'
        }
    }

    /**
     * =========================
     * STOP CAMERA
     * =========================
     */
    stopCamera() {
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach(track => track.stop())
            this.cameraStream = null
        }

        this.status.camera = false
        this.status.microphone = false
    }

    /**
     * =========================
     * STOP SCREEN SHARE
     * =========================
     */
    stopScreenShare() {
        if (this.screenStream) {
            this.screenStream.getTracks().forEach(track => track.stop())
            this.screenStream = null
        }

        this.status.screen = false
        this.status.fullScreenShared = false
    }

    /**
     * =========================
     * CLEANUP ALL
     * =========================
     */
    cleanup() {
        this.stopCamera()
        this.stopScreenShare()
    }

    /**
     * =========================
     * GET STATUS
     * =========================
     */
    getStatus() {
        return this.status
    }
}

export default new AutoProctorService()